import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  useTheme,
  alpha,
  Chip,
  LinearProgress,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Fade
} from '@mui/material';
import {
  Factory as FactoryIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  LocalDrink as LocalDrinkIcon,
  Science as ScienceIcon,
  Speed as SpeedIcon,
  Business as BusinessIcon,
  Timeline as TimelineIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';

const ProductionOverview = () => {
  const { companyId } = useParams();
  const theme = useTheme();
  const { canAccessPage } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState({
    totalBatches: 0,
    completedBatches: 0,
    inProgressBatches: 0,
    draftBatches: 0,
    totalProductionValue: 0,
    totalProductionVolume: 0,
    averageBatchSize: 0,
    topProducts: [],
    recentBatches: [],
    costAnalysis: {
      totalCosts: 0,
      averageCostPerBatch: 0,
      materialCosts: 0,
      laborCosts: 0,
      overheadCosts: 0
    },
    qualityMetrics: {
      averageYieldPercentage: 0,
      batchesWithDeviations: 0,
      averageCompletionTime: 0,
      qcPassRate: 0
    },
    resourceUtilization: {
      activeLocations: 0,
      uniqueFormulations: 0,
      totalIngredients: 0,
      averageIngredientsPerBatch: 0
    },
    batchLogAnalytics: {
      completedMixingLogs: 0,
      totalBatchLogs: 0
    }
  });

  useEffect(() => {
    if (companyId) {
      loadOverviewData();
    }
  }, [companyId]);

  const loadOverviewData = async () => {
    try {
      setLoading(true);
      
      // Load batch data with related information
      const { data: batches, error: batchesError } = await supabase
        .from('production_batches')
        .select(`
          *,
          product:product_id(id, name),
          formulation:formulation_id(id, name, version),
          batch_costs(*),
          batch_ingredients(
            id,
            planned_amount,
            actual_amount,
            planned_unit,
            raw_materials:raw_material_id(name)
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (batchesError) throw batchesError;

      // Load yield summaries
      const { data: yieldData, error: yieldError } = await supabase
        .from('yield_summary')
        .select('*')
        .order('created_at', { ascending: false });

      if (yieldError) console.warn('Error loading yield data:', yieldError);

      // Load batch logs for completion tracking
      const { data: batchLogs, error: logsError } = await supabase
        .from('batch_logs')
        .select('batch_id, batch_log_completed, start_time, created_at, updated_at')
        .eq('company_id', companyId);

      if (logsError) console.warn('Error loading batch logs:', logsError);

             // Load QC tests directly from qc_tests table
       const { data: qcChecks, error: qcError } = await supabase
         .from('qc_tests')
         .select('*')
         .eq('company_id', companyId);

       if (qcError) console.warn('Error loading QC checks:', qcError);

      // Load deviations
      const { data: deviations, error: deviationsError } = await supabase
        .from('batch_log_deviations')
        .select('batch_log_id, status');

      if (deviationsError) console.warn('Error loading deviations:', deviationsError);

      // Process the data
      const processedData = processBatchData(batches || [], yieldData || [], batchLogs || [], qcChecks || [], deviations || []);
      setOverviewData(processedData);

    } catch (error) {
      console.error('Error loading production overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const processBatchData = (batches, yieldData, batchLogs, qcChecks, deviations) => {
    const totalBatches = batches.length;
    const completedBatches = batches.filter(b => b.status === 'completed').length;
    const inProgressBatches = batches.filter(b => ['in_production', 'quality_testing'].includes(b.status)).length;
    const draftBatches = batches.filter(b => b.status === 'draft').length;

    // Calculate production volume and costs
    let totalProductionValue = 0;
    let totalProductionVolume = 0;
    let totalCosts = 0;
    let materialCosts = 0;
    let laborCosts = 0;
    let overheadCosts = 0;

    batches.forEach(batch => {
      if (batch.units_to_make) {
        totalProductionVolume += batch.units_to_make;
      }
      
      // Handle batch_costs - can be array or single object
      if (batch.batch_costs) {
        // Handle if it's an array
        if (Array.isArray(batch.batch_costs) && batch.batch_costs.length > 0) {
          batch.batch_costs.forEach(cost => {
            const batchTotalCost = parseFloat(cost.total_cost) || 0;
            const batchMaterialCost = parseFloat(cost.total_ingredient_cost) || 0;
            const batchLaborCost = parseFloat(cost.labor_cost) || 0;
            const batchOverheadCost = parseFloat(cost.overhead_cost) || 0;
            
            totalCosts += batchTotalCost;
            materialCosts += batchMaterialCost;
            laborCosts += batchLaborCost;
            overheadCosts += batchOverheadCost;
          });
        }
        // Handle if it's a single object (which it appears to be)
        else if (typeof batch.batch_costs === 'object' && batch.batch_costs.total_cost !== undefined) {
          const cost = batch.batch_costs;
          const batchTotalCost = parseFloat(cost.total_cost) || 0;
          const batchMaterialCost = parseFloat(cost.total_ingredient_cost) || 0;
          const batchLaborCost = parseFloat(cost.labor_cost) || 0;
          const batchOverheadCost = parseFloat(cost.overhead_cost) || 0;
          
          totalCosts += batchTotalCost;
          materialCosts += batchMaterialCost;
          laborCosts += batchLaborCost;
          overheadCosts += batchOverheadCost;
        }
      }
    });

    const averageBatchSize = totalBatches > 0 ? totalProductionVolume / totalBatches : 0;
    const averageCostPerBatch = totalBatches > 0 ? totalCosts / totalBatches : 0;
    
    // Top products by batch count
    const productCounts = {};
    batches.forEach(batch => {
      if (batch.product?.name) {
        productCounts[batch.product.name] = (productCounts[batch.product.name] || 0) + 1;
      }
    });

    const topProducts = Object.entries(productCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));

    // Recent batches (last 5)
    const recentBatches = batches.slice(0, 5).map(batch => ({
      id: batch.id,
      batch_number: batch.batch_number,
      product_name: batch.product?.name || 'Unknown Product',
      status: batch.status,
      planned_quantity: batch.planned_quantity,
      planned_unit_of_measure: batch.planned_unit_of_measure,
      created_at: batch.created_at,
      priority: batch.priority
    }));

    // Quality metrics
    let averageYieldPercentage = 0;
    let totalYieldRecords = 0;
    
    yieldData.forEach(yieldRecord => {
      if (yieldRecord.yield_percentage !== null && yieldRecord.yield_percentage !== undefined) {
        averageYieldPercentage += yieldRecord.yield_percentage;
        totalYieldRecords++;
      }
    });
    
    if (totalYieldRecords > 0) {
      averageYieldPercentage = averageYieldPercentage / totalYieldRecords;
    }

    // Calculate completion times (from start_time to updated_at for completed logs)
    let totalCompletionTime = 0;
    let completionTimeCount = 0;
    
    batchLogs.forEach(log => {
      if (log.batch_log_completed && log.start_time && log.updated_at) {
        const startTime = new Date(log.start_time);
        const endTime = new Date(log.updated_at);
        const durationMinutes = (endTime - startTime) / (1000 * 60); // Convert to minutes
        if (durationMinutes > 0) {
          totalCompletionTime += durationMinutes;
          completionTimeCount++;
        }
      }
    });
    
    const averageCompletionTime = completionTimeCount > 0 ? totalCompletionTime / completionTimeCount : 0;

    // QC pass rate calculation using final_status field
    const totalQCChecks = qcChecks ? qcChecks.length : 0;
    const passedQCChecks = qcChecks ? qcChecks.filter(qc => qc.final_status === 'PASS').length : 0;
    const qcPassRate = totalQCChecks > 0 ? (passedQCChecks / totalQCChecks) * 100 : 0;
    
    // Batches with deviations
    const batchLogIdsWithDeviations = new Set(deviations.map(d => d.batch_log_id));
    const batchesWithDeviations = batchLogIdsWithDeviations.size;

    // Resource utilization
    const uniqueLocations = new Set(batches.map(b => b.production_location).filter(Boolean)).size;
    const uniqueFormulations = new Set(batches.map(b => b.formulation_id).filter(Boolean)).size;
    
    let totalIngredients = 0;
    let batchesWithIngredients = 0;
    
    batches.forEach(batch => {
      if (batch.batch_ingredients && Array.isArray(batch.batch_ingredients) && batch.batch_ingredients.length > 0) {
        totalIngredients += batch.batch_ingredients.length;
        batchesWithIngredients++;
      }
    });
    
    const averageIngredientsPerBatch = batchesWithIngredients > 0 ? totalIngredients / batchesWithIngredients : 0;

    return {
      totalBatches,
      completedBatches,
      inProgressBatches,
      draftBatches,
      totalProductionValue,
      totalProductionVolume,
      averageBatchSize,
      topProducts,
      recentBatches,
      costAnalysis: {
        totalCosts,
        averageCostPerBatch,
        materialCosts,
        laborCosts,
        overheadCosts
      },
      qualityMetrics: {
        averageYieldPercentage,
        batchesWithDeviations,
        averageCompletionTime,
        qcPassRate
      },
      resourceUtilization: {
        activeLocations: uniqueLocations,
        uniqueFormulations,
        totalIngredients,
        averageIngredientsPerBatch
      },
      batchLogAnalytics: {
        completedMixingLogs: batchLogs.filter(log => log.batch_log_completed === true).length,
        totalBatchLogs: batchLogs.length
      }
    };
  };

  const formatCurrency = (amount) => `$${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatNumber = (num) => (num || 0).toLocaleString();
  const formatDecimal = (num, decimals = 1) => (num || 0).toFixed(decimals);

  const getStatusColor = (status) => {
    const colors = {
      completed: 'success',
      in_production: 'warning',
      quality_testing: 'info',
      draft: 'default',
      scheduled: 'primary',
      approved: 'info',
      on_hold: 'warning',
      cancelled: 'error',
      failed: 'error'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'default',
      normal: 'primary',
      high: 'warning',
      urgent: 'error'
    };
    return colors[priority] || 'default';
  };

  // Permission check for production overview
  if (!canAccessPage('production_overview')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssessmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Batch Production Overview Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Batch Production Overview. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mb: 2
              }}>
                <AssessmentIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 500,
              color: 'primary.main',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Batch Production Overview
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing manufacturing performance and batch analytics...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'primary.main',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Fade in={true} timeout={800} easing="cubic-bezier(0.4, 0, 0.2, 1)">
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 3, pb: 16 }}>
      {/* Enhanced Header */}
      <Box sx={{ 
        display: 'flex', 
        mt: 5,
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        mb: 3,
        p: 3,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          borderRadius: '50%',
          transform: 'translate(50%, -50%)',
          zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: 'info.main',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 }
              }
            }} />
            <Typography variant="caption" sx={{ 
              color: 'info.main', 
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontSize: '0.75rem'
            }}>
              Performance Insights
            </Typography>
          </Box>
          
          <Typography variant="h4" sx={{ 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '1.8rem',
            mb: 1,
            lineHeight: 1.2
          }}>
            Batch Production Overview
          </Typography>
          
          <Typography variant="body2" sx={{ 
            color: 'text.secondary', 
            fontWeight: 500,
            fontSize: '0.9rem',
            mb: 2.5,
            maxWidth: '400px'
          }}>
            Real-time manufacturing performance and batch analytics with quality tracking
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip 
              label={`${overviewData.totalBatches} ${overviewData.totalBatches === 1 ? 'Batch' : 'Batches'}`} 
              size="small" 
              sx={{ 
                bgcolor: alpha(theme.palette.info.main, 0.1),
                color: 'info.main',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
              }} 
            />
            <Chip 
              label={`${overviewData.completedBatches} Completed`} 
              size="small" 
              sx={{ 
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: 'success.main',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
              }} 
            />
            <Chip 
              label={`${overviewData.inProgressBatches} Active`} 
              size="small" 
              sx={{ 
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                color: 'warning.main',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
              }} 
            />
          </Box>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Row 1: Key Metrics */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '50%', 
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'inline-flex',
                mb: 2
              }}>
                <FactoryIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                {formatNumber(overviewData.totalBatches)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total Batches
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                <Chip 
                  label={`${overviewData.completedBatches} Complete`}
                  color="success"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
                <Chip 
                  label={`${overviewData.inProgressBatches} Active`}
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '50%', 
                bgcolor: alpha(theme.palette.success.main, 0.1),
                display: 'inline-flex',
                mb: 2
              }}>
                <AttachMoneyIcon sx={{ fontSize: 32, color: theme.palette.success.main }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                {formatCurrency(overviewData.costAnalysis.totalCosts)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total Production Costs
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Avg per batch: {formatCurrency(overviewData.costAnalysis.averageCostPerBatch)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '50%', 
                bgcolor: alpha(theme.palette.info.main, 0.1),
                display: 'inline-flex',
                mb: 2
              }}>
                <LocalDrinkIcon sx={{ fontSize: 32, color: theme.palette.info.main }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                {formatNumber(overviewData.totalProductionVolume)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total Units Produced
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Avg batch: {formatNumber(overviewData.averageBatchSize)} units
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '50%', 
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                display: 'inline-flex',
                mb: 2
              }}>
                <AssessmentIcon sx={{ fontSize: 32, color: theme.palette.warning.main }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                {formatDecimal(overviewData.qualityMetrics.averageYieldPercentage)}%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Average Yield
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                QC Pass Rate: {formatDecimal(overviewData.qualityMetrics.qcPassRate)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Row 2: Recent Batches */}
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <TimelineIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Recent Production Activity
                </Typography>
              </Stack>
              
              {overviewData.recentBatches.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <FactoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                    No recent batches
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start creating production batches to see activity here
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {overviewData.recentBatches.map((batch) => (
                    <Box key={batch.id} sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      p: 3,
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                      bgcolor: 'white',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                        transform: 'translateX(4px)',
                        borderColor: alpha(theme.palette.primary.main, 0.3)
                      },
                      transition: 'all 0.2s ease'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Box sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: alpha(getStatusColor(batch.status) === 'success' ? theme.palette.success.main : 
                                       getStatusColor(batch.status) === 'warning' ? theme.palette.warning.main :
                                       getStatusColor(batch.status) === 'error' ? theme.palette.error.main :
                                       theme.palette.primary.main, 0.1),
                          color: getStatusColor(batch.status) === 'success' ? theme.palette.success.main : 
                                 getStatusColor(batch.status) === 'warning' ? theme.palette.warning.main :
                                 getStatusColor(batch.status) === 'error' ? theme.palette.error.main :
                                 theme.palette.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 3
                        }}>
                          <LocalDrinkIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                            {batch.batch_number}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {batch.product_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {batch.planned_quantity} {batch.planned_unit_of_measure} • {new Date(batch.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={batch.status.replace(/_/g, ' ').toUpperCase()}
                          color={getStatusColor(batch.status)}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                        {batch.priority !== 'normal' && (
                          <Chip 
                            label={batch.priority.toUpperCase()}
                            color={getPriorityColor(batch.priority)}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Row 3: Top Products by Batch Count */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <TrendingUpIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Top Products by Batches
                </Typography>
              </Stack>
              
              {overviewData.topProducts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No production data available
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {overviewData.topProducts.map((product, index) => (
                    <Box key={product.name} sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        transform: 'translateX(4px)'
                      },
                      transition: 'all 0.2s ease'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: theme.palette.primary.main,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          fontWeight: 500
                        }}>
                          {index + 1}
                        </Box>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {product.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Product
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                          {product.count}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          batches
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Row 3: Cost Breakdown */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <AttachMoneyIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Average Cost Breakdown
                </Typography>
              </Stack>
              
              <Stack spacing={3}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Avg Material Costs
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatCurrency(overviewData.totalBatches > 0 ? overviewData.costAnalysis.materialCosts / overviewData.totalBatches : 0)}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={overviewData.costAnalysis.totalCosts > 0 ? (overviewData.costAnalysis.materialCosts / overviewData.costAnalysis.totalCosts) * 100 : 0}
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.grey[300], 0.3),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: theme.palette.success.main,
                        borderRadius: 4
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Avg Labor Costs
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatCurrency(overviewData.totalBatches > 0 ? overviewData.costAnalysis.laborCosts / overviewData.totalBatches : 0)}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={overviewData.costAnalysis.totalCosts > 0 ? (overviewData.costAnalysis.laborCosts / overviewData.costAnalysis.totalCosts) * 100 : 0}
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.grey[300], 0.3),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: theme.palette.warning.main,
                        borderRadius: 4
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Avg Overhead Costs
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatCurrency(overviewData.totalBatches > 0 ? overviewData.costAnalysis.overheadCosts / overviewData.totalBatches : 0)}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={overviewData.costAnalysis.totalCosts > 0 ? (overviewData.costAnalysis.overheadCosts / overviewData.costAnalysis.totalCosts) * 100 : 0}
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.grey[300], 0.3),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: theme.palette.info.main,
                        borderRadius: 4
                      }
                    }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Row 4: Quality Metrics */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Quality Metrics
                </Typography>
              </Stack>
              
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 1 }}>
                      {formatDecimal(overviewData.qualityMetrics.averageYieldPercentage)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      AVG YIELD
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.info.main, mb: 1 }}>
                      {formatDecimal(overviewData.qualityMetrics.qcPassRate)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      QC PASS RATE
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.warning.main, mb: 1 }}>
                      {overviewData.qualityMetrics.batchesWithDeviations}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      DEVIATIONS
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 1 }}>
                      {formatDecimal(overviewData.qualityMetrics.averageCompletionTime / 60)}h
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      AVG TIME
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Row 4: Production Efficiency */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <SpeedIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Production Efficiency
                </Typography>
              </Stack>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ 
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.error.main, 0.04),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.error.main, mb: 0.5 }}>
                      {overviewData.qualityMetrics.batchesWithDeviations}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      BATCHES WITH ISSUES
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ 
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.04),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 0.5 }}>
                      {overviewData.totalBatches - overviewData.qualityMetrics.batchesWithDeviations}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      CLEAN BATCHES
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                      Success Rate
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={overviewData.totalBatches > 0 ? ((overviewData.totalBatches - overviewData.qualityMetrics.batchesWithDeviations) / overviewData.totalBatches) * 100 : 100}
                      sx={{ 
                        height: 12, 
                        borderRadius: 6,
                        bgcolor: alpha(theme.palette.grey[300], 0.3),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: theme.palette.success.main,
                          borderRadius: 6
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {overviewData.totalBatches > 0 ? Math.round(((overviewData.totalBatches - overviewData.qualityMetrics.batchesWithDeviations) / overviewData.totalBatches) * 100) : 100}% batches without issues
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>





        {/* Row 5: Status Distribution */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <AssignmentIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Batch Status
                </Typography>
              </Stack>
              
              <Stack spacing={2}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Completed
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                    {overviewData.completedBatches}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    In Progress
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.warning.main }}>
                    {overviewData.inProgressBatches}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.grey[400], 0.04),
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.1)}`
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Draft
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    {overviewData.draftBatches}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>







        {/* Row 5: Material Usage Insights */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
                <ScienceIcon sx={{ fontSize: 32, color: 'text.primary', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Material Intelligence
                </Typography>
              </Stack>
              
              <Stack spacing={3}>
                <Box sx={{ 
                  p: 3,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.info.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                  textAlign: 'center'
                }}>
                  <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.info.main, mb: 1 }}>
                    {formatDecimal(overviewData.resourceUtilization.averageIngredientsPerBatch)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                    Avg Ingredients per Batch
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {overviewData.resourceUtilization.totalIngredients} total ingredients used
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                      textAlign: 'center'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 0.5 }}>
                        {overviewData.resourceUtilization.uniqueFormulations}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        FORMULATIONS
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      textAlign: 'center'
                    }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.primary.main, mb: 0.5 }}>
                        {overviewData.resourceUtilization.activeLocations}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        LOCATIONS
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        </Grid>



      </Grid>
      </Container>
    </Fade>
  );
};

export default ProductionOverview;
